import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import BrowseLayout from '../components/home/BrowseLayout';
import './SetsPage.css';
import { LoadingContext } from '../App';
import { usePostHog } from '@posthog/react';

const setSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const SetsPage = () => {
    const [seriesMap, setSeriesMap] = useState({});
    const [expandedSeries, setExpandedSeries] = useState({});
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const { setLoading } = useContext(LoadingContext);
    const posthog = usePostHog();

    useEffect(() => {
        const fetchSets = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${apiBaseUrl}/sets`);
                if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
                const data = await response.json();

                const grouped = data.reduce((acc, set) => {
                    if (!acc[set.series]) acc[set.series] = [];
                    acc[set.series].push(set);
                    return acc;
                }, {});

                const filteredSeries = Object.entries(grouped).reduce((acc, [series, sets]) => {
                    if (['pop', 'other', 'np'].some((bad) => series.toLowerCase().includes(bad))) return acc;
                    const filtered = sets.filter((s) =>
                        !s.name.toLowerCase().includes('promos') &&
                        !s.name.toLowerCase().includes('energies') &&
                        !s.name.toLowerCase().includes('starter')
                    );
                    const validSets = filtered.length > 0 ? filtered : sets;
                    const headerSet = validSets.slice().sort((a, b) => new Date(a.release_date) - new Date(b.release_date))[0];
                    const fullList = sets.slice().sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
                    acc[series] = { headerSet, fullList };
                    return acc;
                }, {});

                setSeriesMap(filteredSeries);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchSets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleSeries = (series) => {
        const isExpanding = !expandedSeries[series];
        setExpandedSeries((prev) => ({ ...prev, [series]: !prev[series] }));
        if (isExpanding) posthog.capture('series_expanded', { series_name: series });
    };

    const handleSetClick = (setName) => {
        posthog.capture('set_clicked', { set_name: setName });
        navigate(`/sets/${setSlug(setName)}`);
    };

    const sortedSeries = Object.values(seriesMap)
        .sort((a, b) => new Date(b.headerSet.release_date) - new Date(a.headerSet.release_date));

    return (
        <BrowseLayout>
            <section className="gh-sets">
                <h1 className="gh-sets-title">Sets</h1>

                {error && <p className="gh-sets-msg">Error: {error}</p>}

                {sortedSeries.map(({ headerSet, fullList }) => {
                    const series = headerSet.series;
                    const open = !!expandedSeries[series];
                    const minY = Math.min(...fullList.map((s) => new Date(s.release_date).getFullYear()));
                    const maxY = Math.max(...fullList.map((s) => new Date(s.release_date).getFullYear()));
                    return (
                        <div className="gh-series" key={series}>
                            <button
                                type="button"
                                className={`gh-series-head ${open ? 'open' : ''}`}
                                onClick={() => toggleSeries(series)}
                                aria-expanded={open}
                            >
                                {headerSet.logo_image && (
                                    <span className="gh-series-logo">
                                        <img src={headerSet.logo_image} alt={series} />
                                    </span>
                                )}
                                <span className="gh-series-name">
                                    {series}
                                    <em>({minY}–{maxY})</em>
                                </span>
                                <span className="gh-series-chev" aria-hidden="true">▾</span>
                            </button>

                            {open && (
                                <div className="gh-setgrid">
                                    {fullList.map((set) => (
                                        <button
                                            type="button"
                                            key={set.id}
                                            className="gh-setcard"
                                            onClick={() => handleSetClick(set.name)}
                                        >
                                            <span className="gh-setcard-logo">
                                                {set.logo_image
                                                    ? <img src={set.logo_image} alt={set.name} />
                                                    : <span className="gh-setcard-noimg">{set.name}</span>}
                                            </span>
                                            <span className="gh-setcard-meta">
                                                <span className="gh-setcard-name">{set.name}</span>
                                                <span className="gh-setcard-year">{new Date(set.release_date).getFullYear()}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </section>
        </BrowseLayout>
    );
};

export default SetsPage;
